// src/pages/MieOrePage.js
// ═══════════════════════════════════════════════════════════════════════════
// LE MIE ORE — a tutto schermo (ottobre 2026, W402)
// ═══════════════════════════════════════════════════════════════════════════
// Una pagina sola, senza barra laterale né pulsanti fissi, pensata per il telefono:
//  • tocchi una commessa → il tempo parte; tocchi un'altra → la prima si ferma da sola e
//    parte la seconda; «Pausa» ferma il tempo e si riprende con un tocco, «Stop» quando si è
//    finito di lavorare; «← Torna a Nexiora» lascia il tempo acceso; «Ufficio / generale» per il tempo di struttura;
//  • sotto, la giornata: totale e ore per commessa, blocchi correggibili con un tocco,
//    «+ Aggiungi a mano» per chi preferisce segnare a fine giornata;
//  • il cronometro rimasto acceso da un giorno precedente chiede «a che ora hai finito?».
// Regole e calcoli: utils/diarioOre.js. Ognuno vede e scrive solo le proprie ore (regole 61).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, query, where, onSnapshot, writeBatch, updateDoc, deleteDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { toLocalDateStr } from "../utils/dateUtils";
import {
  UFFICIO, giornoDi, istante, oraDi, minutiBlocco, durata, cronometro, riepilogoGiornata,
  commesseProposte, cercaCommesse, aggiornaRecenti, erroreBlocco, sovrapposti,
  minutiCartellino, idCartellino, riscontroCartellino,
} from "../utils/diarioOre";

const VIOLA = "#7c3aed", SCURO = "#1e1b4b", GRIGIO = "#6b7280";
const COLORI = ["#7c3aed", "#0d9488", "#2563eb", "#d97706", "#db2777", "#059669", "#4f46e5", "#b45309"];
const font = "'Inter',sans-serif";

const chiaveRecenti = (uid) => `nexiora_ore_recenti_${uid}`;
// «Pausa» ricorda su cosa si stava lavorando, per riprendere con un tocco (W404)
const chiavePausa = (uid) => `nexiora_ore_pausa_${uid}`;
const leggiPausa = (uid) => { try { return JSON.parse(localStorage.getItem(chiavePausa(uid)) || "null"); } catch { return null; } };
const scriviPausa = (uid, v) => { try { if (v) localStorage.setItem(chiavePausa(uid), JSON.stringify(v)); else localStorage.removeItem(chiavePausa(uid)); } catch { /* */ } };
const leggiRecenti = (uid) => { try { return JSON.parse(localStorage.getItem(chiaveRecenti(uid)) || "[]"); } catch { return []; } };
const scriviRecenti = (uid, v) => { try { localStorage.setItem(chiaveRecenti(uid), JSON.stringify(v)); } catch { /* */ } };

const spostaGiorno = (iso, n) => { const [a, m, g] = iso.split("-").map(Number); return toLocalDateStr(new Date(a, m - 1, g + n)); };
const giornoLeggibile = (iso, oggi) => {
  if (iso === oggi) return "Oggi";
  if (iso === spostaGiorno(oggi, -1)) return "Ieri";
  const [a, m, g] = iso.split("-").map(Number);
  return new Date(a, m - 1, g).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
};
const etichetta = (p) => (p ? `${p.codiceCommessa ? p.codiceCommessa + " · " : ""}${p.name || "Commessa"}` : UFFICIO.nome);

// ── Finestra di modifica / inserimento a mano ────────────────────────────────
function FinestraBlocco({ blocco, giorno, progetti, altri, onChiudi, onSalva, onElimina }) {
  const nuovo = !blocco?.id;
  const [projectId, setProjectId] = useState(blocco?.projectId || "");
  const [cerca, setCerca] = useState("");
  const [modo, setModo] = useState(blocco && !blocco.inizio && blocco.minuti ? "durata" : "orario");
  const [dalle, setDalle] = useState(blocco?.inizio ? oraDi(blocco.inizio) : "09:00");
  const [alle, setAlle] = useState(blocco?.fine ? oraDi(blocco.fine) : blocco?.inCorso ? oraDi(Date.now()) : "10:00");
  const [ore, setOre] = useState(blocco?.minuti ? String(Math.floor(blocco.minuti / 60)) : "1");
  const [min, setMin] = useState(blocco?.minuti ? String(blocco.minuti % 60) : "0");
  const [nota, setNota] = useState(blocco?.nota || "");
  const [errore, setErrore] = useState("");
  const inCorso = !!blocco?.inCorso;
  const elenco = useMemo(() => cercaCommesse(progetti, cerca), [progetti, cerca]);
  const scelto = progetti.find(p => p.id === projectId);

  const salva = () => {
    const dati = modo === "durata"
      ? { inizio: null, fine: null, minuti: (parseInt(ore, 10) || 0) * 60 + (parseInt(min, 10) || 0) }
      : { inizio: istante(giorno, dalle), fine: inCorso ? null : istante(giorno, alle), minuti: null };
    const e = inCorso ? (dati.inizio > Date.now() ? "L'ora di inizio non può essere nel futuro." : "") : erroreBlocco(dati);
    if (e) { setErrore(e); return; }
    onSalva({ ...dati, projectId, projectNome: scelto?.name || "", codiceCommessa: scelto?.codiceCommessa || "", nota: nota.trim() });
  };
  const prova = modo === "orario" && !inCorso ? { id: blocco?.id, inizio: istante(giorno, dalle), fine: istante(giorno, alle) } : null;
  const sopra = prova && prova.fine > prova.inizio ? sovrapposti(prova, altri) : [];

  const campo = { width: "100%", boxSizing: "border-box", padding: "11px 12px", border: "1.5px solid #ddd6fe", borderRadius: 10, fontSize: 16, fontFamily: font, background: "#fff" };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onChiudi()} style={{ zIndex: 1100, alignItems: "flex-end", padding: 0 }}>
      <div style={{ background: "#fff", width: "100%", maxWidth: 560, margin: "0 auto", borderRadius: "18px 18px 0 0", padding: "18px 18px 22px", maxHeight: "92vh", overflowY: "auto", fontFamily: font }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 800, color: SCURO }}>{nuovo ? "Aggiungi ore a mano" : inCorso ? "Blocco in corso" : "Correggi il blocco"}</div>
          <button type="button" onClick={onChiudi} aria-label="Chiudi" style={{ border: "none", background: "#f3f4f6", width: 34, height: 34, borderRadius: "50%", fontSize: 16, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: GRIGIO, marginBottom: 6 }}>SU COSA</div>
        <div style={{ padding: "10px 12px", border: `1.5px solid ${VIOLA}`, borderRadius: 10, fontSize: 14.5, fontWeight: 700, color: SCURO, marginBottom: 8 }}>{etichetta(scelto)}</div>
        <input value={cerca} onChange={e => setCerca(e.target.value)} placeholder="🔍 Cambia: cerca numero, nome o cliente" style={{ ...campo, fontSize: 14 }} />
        <div style={{ maxHeight: 170, overflowY: "auto", border: "1px solid #f3f4f6", borderRadius: 10, marginTop: 6 }}>
          {[null, ...elenco].map(p => (
            <button key={p?.id || "ufficio"} type="button" onClick={() => setProjectId(p?.id || "")}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", borderBottom: "1px solid #f9fafb", fontFamily: font, fontSize: 13.5, cursor: "pointer",
                background: (p?.id || "") === projectId ? "#f5f3ff" : "#fff", color: (p?.id || "") === projectId ? VIOLA : "#111827", fontWeight: (p?.id || "") === projectId ? 700 : 500 }}>
              {p ? etichetta(p) : `🏢 ${UFFICIO.nome}`}{p?.clienteNome ? <span style={{ color: "#9ca3af", fontWeight: 400 }}> — {p.clienteNome}</span> : null}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: GRIGIO, margin: "14px 0 6px" }}>QUANTO</div>
        {!inCorso && (
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            {[["orario", "Dalle… alle…"], ["durata", "Solo la durata"]].map(([k, t]) => (
              <button key={k} type="button" onClick={() => setModo(k)} style={{ flex: 1, padding: "8px 10px", borderRadius: 9, fontFamily: font, fontSize: 13, fontWeight: 700, cursor: "pointer",
                border: `1.5px solid ${modo === k ? VIOLA : "#e5e7eb"}`, background: modo === k ? "#f5f3ff" : "#fff", color: modo === k ? VIOLA : GRIGIO }}>{t}</button>
            ))}
          </div>
        )}
        {modo === "orario" ? (
          <div style={{ display: "flex", gap: 10 }}>
            <label style={{ flex: 1, fontSize: 12, color: GRIGIO }}>Dalle<input type="time" value={dalle} onChange={e => setDalle(e.target.value)} style={campo} /></label>
            {inCorso
              ? <div style={{ flex: 1, fontSize: 12, color: GRIGIO }}>Alle<div style={{ ...campo, color: VIOLA, fontWeight: 700 }}>in corso</div></div>
              : <label style={{ flex: 1, fontSize: 12, color: GRIGIO }}>Alle<input type="time" value={alle} onChange={e => setAlle(e.target.value)} style={campo} /></label>}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <label style={{ flex: 1, fontSize: 12, color: GRIGIO }}>Ore<input type="number" inputMode="numeric" min="0" max="24" value={ore} onChange={e => setOre(e.target.value)} style={campo} /></label>
            <label style={{ flex: 1, fontSize: 12, color: GRIGIO }}>Minuti<select value={min} onChange={e => setMin(e.target.value)} style={campo}>{[0, 15, 30, 45].concat(parseInt(min, 10) % 15 ? [parseInt(min, 10)] : []).map(m => <option key={m} value={String(m)}>{m}</option>)}</select></label>
          </div>
        )}
        {sopra.length > 0 && <div style={{ fontSize: 12, color: "#92400e", marginTop: 6 }}>Attenzione: si sovrappone a {sopra.length === 1 ? "un altro blocco" : `${sopra.length} altri blocchi`} della giornata.</div>}

        <div style={{ fontSize: 12, fontWeight: 700, color: GRIGIO, margin: "14px 0 6px" }}>NOTA <span style={{ fontWeight: 400 }}>(facoltativa)</span></div>
        <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Es. tavole impianto di terra" style={campo} />

        {errore && <div style={{ fontSize: 13, color: "#dc2626", fontWeight: 600, marginTop: 10 }}>{errore}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          {!nuovo && <button type="button" onClick={onElimina} style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", fontWeight: 700, fontFamily: font, fontSize: 14, cursor: "pointer" }}>Elimina</button>}
          <button type="button" onClick={onChiudi} style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", color: GRIGIO, fontWeight: 700, fontFamily: font, fontSize: 14, cursor: "pointer" }}>Annulla</button>
          <button type="button" onClick={salva} style={{ flex: 1.4, padding: "12px 14px", borderRadius: 10, border: "none", background: VIOLA, color: "#fff", fontWeight: 800, fontFamily: font, fontSize: 14, cursor: "pointer" }}>Salva</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function MieOrePage({ onEsci }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const { addToast } = useToast();
  const uid = currentUser?.uid;
  const [adesso, setAdesso] = useState(Date.now());
  const oggi = giornoDi(adesso);
  const [giorno, setGiorno] = useState(oggi);
  const [blocchi, setBlocchi] = useState(null);
  const [inCorso, setInCorso] = useState(null);       // il blocco col cronometro acceso (qualunque giorno)
  const [progetti, setProgetti] = useState([]);
  const [recenti, setRecenti] = useState(() => leggiRecenti(uid));
  const [inPausa, setInPausa] = useState(() => leggiPausa(uid));   // { projectId, projectNome, codiceCommessa, dal }
  const segnaPausa = (v) => { setInPausa(v); scriviPausa(uid, v); };
  // l'ordine dei pulsanti resta quello dell'apertura: se si spostassero a ogni tocco, il
  // dito troverebbe la commessa sbagliata
  const [ordinePulsanti] = useState(() => leggiRecenti(uid));
  const [cercaAperta, setCercaAperta] = useState(false);
  const [cerca, setCerca] = useState("");
  const [modifica, setModifica] = useState(null);     // blocco da modificare, {} per nuovo
  const [oraFine, setOraFine] = useState("18:00");
  const [occupato, setOccupato] = useState(false);

  // a tutto schermo: niente pulsanti fissi in basso
  useEffect(() => { document.body.classList.add("nx-schermo-intero"); return () => document.body.classList.remove("nx-schermo-intero"); }, []);
  // il cronometro avanza ogni secondo
  useEffect(() => { const t = setInterval(() => setAdesso(Date.now()), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!orgId) return undefined;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", orgId)),
      s => setProgetti(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setProgetti([]));
  }, [orgId]);
  useEffect(() => {
    if (!orgId || !uid) return undefined;
    setBlocchi(null);
    return onSnapshot(query(collection(db, "diarioOre"), where("orgId", "==", orgId), where("uid", "==", uid), where("data", "==", giorno)),
      s => setBlocchi(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setBlocchi([]));
  }, [orgId, uid, giorno]);
  useEffect(() => {
    if (!orgId || !uid) return undefined;
    return onSnapshot(query(collection(db, "diarioOre"), where("orgId", "==", orgId), where("uid", "==", uid), where("inCorso", "==", true)),
      s => {
        const l = s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.inizio || 0) - (a.inizio || 0));
        setInCorso(l[0] || null);
      }, () => setInCorso(null));
  }, [orgId, uid]);

  // Cartellino del mese del giorno guardato: per il riscontro gentile e per sapere se il
  // mese è già approvato (dopo l'approvazione le ore si correggono solo tramite
  // l'amministrazione: regole 62)
  const [cartellino, setCartellino] = useState(null);
  useEffect(() => {
    if (!orgId || !uid) return undefined;
    return onSnapshot(doc(db, "presenze", idCartellino(orgId, uid, giorno)),
      s => setCartellino(s.exists() ? s.data() : null), () => setCartellino(null));
  }, [orgId, uid, giorno]);
  const meseApprovato = cartellino?.stato === "approvata";
  const minutiPresenza = minutiCartellino(cartellino?.giorni?.[Number(giorno.slice(8, 10))]?.timbrature || []);

  const proposte = useMemo(() => commesseProposte(progetti, { recenti: ordinePulsanti, uid }), [progetti, ordinePulsanti, uid]);
  const risultati = useMemo(() => cercaCommesse(progetti, cerca), [progetti, cerca]);
  const giornata = useMemo(() => riepilogoGiornata(blocchi || [], adesso), [blocchi, adesso]);
  const dimenticato = inCorso && inCorso.data && inCorso.data < oggi;   // acceso da un giorno precedente
  const colore = (projectId) => { const i = proposte.findIndex(p => p.id === projectId); return projectId ? COLORI[(i >= 0 ? i : 7) % COLORI.length] : "#64748b"; };

  const base = () => ({ orgId, uid, nomeUtente: currentUser?.displayName || currentUser?.email || "", updatedAt: serverTimestamp() });

  /** Ferma il blocco in corso. Meno di un minuto è un tocco per sbaglio: il blocco si toglie. */
  const chiudi = (b, blocco, ora) => {
    if (ora - (blocco.inizio || ora) < 60000) b.delete(doc(db, "diarioOre", blocco.id));
    else b.update(doc(db, "diarioOre", blocco.id), { fine: ora, inCorso: false, updatedAt: serverTimestamp() });
  };

  /** Tocco su una commessa (o su Ufficio): ferma il blocco in corso e ne fa partire uno nuovo. */
  const avvia = async (p) => {
    if (occupato || dimenticato) return;
    const projectId = p?.id || "";
    if (inCorso && (inCorso.projectId || "") === projectId) return; // è già lei
    setOccupato(true);
    try {
      const ora = Date.now();
      const b = writeBatch(db);
      if (inCorso) chiudi(b, inCorso, ora);
      b.set(doc(collection(db, "diarioOre")), { ...base(), data: giornoDi(ora), projectId, projectNome: p?.name || "", codiceCommessa: p?.codiceCommessa || "",
        inizio: ora, fine: null, minuti: null, inCorso: true, nota: "", origine: "cronometro", createdAt: serverTimestamp() });
      await b.commit();
      segnaPausa(null);
      if (projectId) { const r = aggiornaRecenti(recenti, projectId); setRecenti(r); scriviRecenti(uid, r); }
      setCercaAperta(false); setCerca("");
      if (giorno !== oggi) setGiorno(oggi);
    } catch { addToast("Non sono riuscito a far partire il tempo: riprova", "error"); }
    finally { setOccupato(false); }
  };

  /** «Pausa»: il tempo si ferma e si riprende con un tocco. «Stop»: finito di lavorare. */
  const ferma = async (conPausa) => {
    if (occupato) return;
    if (!inCorso) { if (!conPausa) segnaPausa(null); return; }
    setOccupato(true);
    try {
      const b = writeBatch(db); chiudi(b, inCorso, Date.now()); await b.commit();
      segnaPausa(conPausa ? { projectId: inCorso.projectId || "", projectNome: inCorso.projectNome || "", codiceCommessa: inCorso.codiceCommessa || "", dal: Date.now() } : null);
      if (!conPausa) addToast("Tempo fermato ✅ Riparte quando tocchi una commessa", "success");
    }
    catch { addToast("Non sono riuscito a fermare il tempo: riprova", "error"); }
    finally { setOccupato(false); }
  };
  const pausa = () => ferma(true);
  const stop = () => ferma(false);
  const riprendi = () => {
    if (!inPausa) return;
    const p = inPausa.projectId ? (progetti.find(x => x.id === inPausa.projectId) || { id: inPausa.projectId, name: inPausa.projectNome, codiceCommessa: inPausa.codiceCommessa }) : null;
    avvia(p);
  };

  /** Cronometro rimasto acceso da un giorno precedente: si chiude all'ora indicata. */
  const chiudiDimenticato = async () => {
    const fine = istante(inCorso.data, oraFine);
    if (fine <= inCorso.inizio) { addToast(`Indica un orario dopo le ${oraDi(inCorso.inizio)}`, "error"); return; }
    try { await updateDoc(doc(db, "diarioOre", inCorso.id), { fine, inCorso: false, updatedAt: serverTimestamp() }); addToast("Sistemato ✅", "success"); }
    catch { addToast("Non salvato: riprova", "error"); }
  };

  const salvaBlocco = async (dati) => {
    try {
      if (modifica?.id) await updateDoc(doc(db, "diarioOre", modifica.id), { ...dati, updatedAt: serverTimestamp() });
      else await addDoc(collection(db, "diarioOre"), { ...base(), ...dati, data: giorno, inCorso: false, origine: "manuale", createdAt: serverTimestamp() });
      if (dati.projectId) { const r = aggiornaRecenti(recenti, dati.projectId); setRecenti(r); scriviRecenti(uid, r); }
      setModifica(null); addToast("Ore salvate ✅", "success");
    } catch { addToast("Non salvato: riprova", "error"); }
  };
  const eliminaBlocco = async () => {
    if (!modifica?.id) return;
    if (!(await confirm("Eliminare questo blocco di ore?", { type: "danger", confirmLabel: "Elimina" }))) return;
    try { await deleteDoc(doc(db, "diarioOre", modifica.id)); setModifica(null); addToast("Blocco eliminato", "success"); }
    catch { addToast("Non eliminato: riprova", "error"); }
  };

  const attivo = inCorso && !dimenticato ? inCorso : null;
  const progettoAttivo = attivo ? progetti.find(p => p.id === attivo.projectId) : null;
  const pulsante = (p) => {
    const id = p?.id || "";
    const sta = attivo && (attivo.projectId || "") === id;
    const c = colore(id);
    return (
      <button key={id || "ufficio"} type="button" onClick={() => avvia(p)} disabled={occupato || !!dimenticato}
        style={{ textAlign: "left", minHeight: 74, padding: "12px 14px", borderRadius: 14, cursor: "pointer", fontFamily: font,
          border: `2px solid ${sta ? c : "#ede9fe"}`, background: sta ? `${c}14` : "#fff", boxShadow: sta ? `0 0 0 3px ${c}22` : "0 1px 2px rgba(0,0,0,.04)",
          display: "flex", flexDirection: "column", justifyContent: "center", gap: 3, opacity: dimenticato ? 0.5 : 1 }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: c, letterSpacing: ".02em" }}>{p ? (p.codiceCommessa || "Commessa") : "🏢 UFFICIO"}{sta ? "  ● in corso" : ""}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: SCURO, lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p ? (p.name || "Commessa") : "Generale, non su una commessa"}</span>
      </button>
    );
  };

  return (
    <div className="nx-mie-ore" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 900, background: "#f8f7fc", overflowY: "auto", fontFamily: font }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "14px 16px 40px" }}>
        {/* testata */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: SCURO }}>⏱️ Le mie ore</div>
            <div style={{ fontSize: 12, color: GRIGIO }}>Le ore per commessa della tua giornata</div>
          </div>
          {/* «Esci» faceva pensare che il tempo si fermasse: si torna a Nexiora e il tempo continua */}
          <button type="button" onClick={onEsci} aria-label="Torna a Nexiora" title={attivo ? "Il tempo continua a contare" : undefined}
            style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #ddd6fe", background: "#fff", fontFamily: font, fontWeight: 700, fontSize: 13, color: VIOLA, cursor: "pointer", textAlign: "center", lineHeight: 1.2 }}>
            ← Torna a Nexiora
            {attivo && <span style={{ display: "block", fontSize: 10.5, fontWeight: 600, color: GRIGIO }}>il tempo continua</span>}
          </button>
        </div>
        <div style={{ marginTop: 10, background: "#faf5ff", color: "#5b21b6", border: "1px solid #ddd6fe", borderLeft: "4px solid #7c3aed", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, lineHeight: 1.45 }}>
          Servono a sapere <b>quanto costa ogni commessa</b>, non a controllare il tuo lavoro. Le presenze e le paghe restano sul cartellino.
        </div>

        {/* cronometro dimenticato acceso */}
        {dimenticato && (
          <div style={{ marginTop: 14, background: "#fffbeb", border: "1.5px solid #fcd34d", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: "#92400e" }}>Il tempo è rimasto acceso da {giornoLeggibile(inCorso.data, oggi).toLowerCase()}</div>
            <div style={{ fontSize: 13, color: "#78350f", marginTop: 3 }}>Su <b>{inCorso.projectId ? etichetta({ codiceCommessa: inCorso.codiceCommessa, name: inCorso.projectNome }) : UFFICIO.nome}</b>, dalle {oraDi(inCorso.inizio)}. A che ora hai finito?</div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input type="time" value={oraFine} onChange={e => setOraFine(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #fcd34d", fontSize: 16, fontFamily: font }} />
              <button type="button" onClick={chiudiDimenticato} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "#d97706", color: "#fff", fontWeight: 800, fontFamily: font, fontSize: 14, cursor: "pointer" }}>Conferma</button>
            </div>
          </div>
        )}

        {/* cosa sta girando */}
        <div style={{ marginTop: 14, background: attivo ? SCURO : "#fff", color: attivo ? "#fff" : SCURO, borderRadius: 18, padding: "16px 18px", border: attivo ? "none" : "1.5px dashed #c4b5fd" }}>
          {attivo ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#c4b5fd", letterSpacing: ".04em" }}>STAI LAVORANDO SU · dalle {oraDi(attivo.inizio)}</div>
                <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>{attivo.projectId ? etichetta(progettoAttivo || { codiceCommessa: attivo.codiceCommessa, name: attivo.projectNome }) : UFFICIO.nome}</div>
                <div style={{ fontSize: 34, fontWeight: 900, fontVariantNumeric: "tabular-nums", marginTop: 2, letterSpacing: "-.01em" }}>{cronometro(adesso - attivo.inizio)}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={pausa} disabled={occupato} title="Ferma il tempo; lo riprendi con un tocco"
                  style={{ padding: "14px 18px", borderRadius: 14, border: "none", background: "#fff", color: SCURO, fontWeight: 900, fontSize: 16, fontFamily: font, cursor: "pointer" }}>⏸ Pausa</button>
                <button type="button" onClick={stop} disabled={occupato} title="Hai finito di lavorare: il tempo si ferma"
                  style={{ padding: "14px 18px", borderRadius: 14, border: "1.5px solid #fca5a5", background: "#fef2f2", color: "#b91c1c", fontWeight: 900, fontSize: 16, fontFamily: font, cursor: "pointer" }}>⏹ Stop</button>
              </div>
            </div>
          ) : inPausa && !dimenticato ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: "#d97706", letterSpacing: ".04em" }}>⏸ IN PAUSA dalle {oraDi(inPausa.dal)}</div>
                {/* quanto dura la pausa (W406): si vede subito se è rimasta aperta troppo */}
                <div style={{ fontSize: 24, fontWeight: 900, color: "#d97706", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{cronometro(adesso - inPausa.dal)}</div>
                <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{inPausa.projectId ? etichetta({ codiceCommessa: inPausa.codiceCommessa, name: inPausa.projectNome }) : UFFICIO.nome}</div>
                <div style={{ fontSize: 12, color: GRIGIO, marginTop: 2 }}>Il tempo è fermo. Riprendi quando torni al lavoro, oppure tocca un'altra commessa.</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" onClick={riprendi} disabled={occupato}
                  style={{ padding: "14px 18px", borderRadius: 14, border: "none", background: VIOLA, color: "#fff", fontWeight: 900, fontSize: 16, fontFamily: font, cursor: "pointer" }}>▶ Riprendi</button>
                <button type="button" onClick={stop} disabled={occupato} title="Hai finito di lavorare"
                  style={{ padding: "14px 18px", borderRadius: 14, border: "1.5px solid #fca5a5", background: "#fef2f2", color: "#b91c1c", fontWeight: 900, fontSize: 16, fontFamily: font, cursor: "pointer" }}>⏹ Stop</button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 14.5, fontWeight: 700, textAlign: "center", color: VIOLA }}>
              {dimenticato ? "Prima sistema il tempo rimasto acceso, qui sopra." : "Tocca la commessa su cui stai lavorando: il tempo parte da solo."}
            </div>
          )}
        </div>

        {/* le commesse */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10, marginTop: 14 }}>
          {proposte.map(pulsante)}
          {pulsante(null)}
          <button type="button" onClick={() => setCercaAperta(v => !v)} disabled={!!dimenticato}
            style={{ minHeight: 74, borderRadius: 14, border: "2px dashed #c4b5fd", background: "#fff", color: VIOLA, fontWeight: 800, fontSize: 14, fontFamily: font, cursor: "pointer" }}>🔍 Altra commessa</button>
        </div>
        {cercaAperta && (
          <div style={{ marginTop: 10, background: "#fff", border: "1px solid #ede9fe", borderRadius: 14, padding: 10 }}>
            <input autoFocus value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Numero, nome o cliente della commessa"
              style={{ width: "100%", boxSizing: "border-box", padding: "11px 12px", border: "1.5px solid #ddd6fe", borderRadius: 10, fontSize: 16, fontFamily: font }} />
            <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 6 }}>
              {risultati.length === 0 ? <div style={{ fontSize: 13, color: "#9ca3af", padding: 10 }}>Nessuna commessa attiva trovata.</div>
                : risultati.map(p => (
                  <button key={p.id} type="button" onClick={() => avvia(p)}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 10px", border: "none", borderBottom: "1px solid #f5f3ff", background: "#fff", fontFamily: font, fontSize: 14, cursor: "pointer", color: SCURO }}>
                    <b style={{ color: VIOLA }}>{p.codiceCommessa || "—"}</b> {p.name}{p.clienteNome ? <span style={{ color: "#9ca3af" }}> — {p.clienteNome}</span> : null}
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* la giornata */}
        <div style={{ marginTop: 22, background: "#fff", border: "1px solid #ede9fe", borderRadius: 16, padding: "14px 14px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button type="button" onClick={() => setGiorno(g => spostaGiorno(g, -1))} aria-label="Giorno prima" title="Il giorno prima: puoi correggere le ore fino all'approvazione del cartellino"
              style={{ border: "1px solid #ddd6fe", background: "#f5f3ff", borderRadius: 9, height: 36, padding: "0 10px", fontSize: 12.5, fontWeight: 800, cursor: "pointer", color: VIOLA, fontFamily: font, whiteSpace: "nowrap" }}>‹ {giorno === oggi ? "Ieri" : "Prima"}</button>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: SCURO, textTransform: "capitalize" }}>{giornoLeggibile(giorno, oggi)}</div>
              <div style={{ fontSize: 12, color: GRIGIO }}>Totale <b style={{ color: SCURO }}>{durata(giornata.totale)}</b>{giornata.totale !== giornata.sulleCommesse ? <> · sulle commesse {durata(giornata.sulleCommesse)}</> : null}</div>
            </div>
            <button type="button" onClick={() => setGiorno(g => spostaGiorno(g, 1))} disabled={giorno >= oggi} aria-label="Giorno dopo"
              style={{ border: `1px solid ${giorno >= oggi ? "#f3f4f6" : "#ddd6fe"}`, background: giorno >= oggi ? "#fff" : "#f5f3ff", borderRadius: 9, height: 36, padding: "0 10px", fontSize: 12.5, fontWeight: 800, cursor: giorno >= oggi ? "default" : "pointer", color: giorno >= oggi ? "#d1d5db" : VIOLA, fontFamily: font, whiteSpace: "nowrap" }}>{giorno >= oggi ? "Oggi" : spostaGiorno(giorno, 1) === oggi ? "Oggi ›" : "Dopo ›"}</button>
          </div>

          {/* barra della giornata per commessa */}
          {giornata.totale > 0 && (
            <>
              <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginTop: 12, background: "#f3f4f6" }}>
                {giornata.perCommessa.map(x => <div key={x.projectId || "u"} title={`${x.nome}: ${durata(x.minuti)}`} style={{ width: `${(x.minuti / giornata.totale) * 100}%`, background: colore(x.projectId) }} />)}
              </div>
              <div style={{ marginTop: 8 }}>
                {giornata.perCommessa.map(x => (
                  <div key={x.projectId || "u"} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 2px", fontSize: 13 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: colore(x.projectId), flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: SCURO }}>{x.codice ? <b>{x.codice} </b> : null}{x.nome}</span>
                    <b style={{ color: SCURO, fontVariantNumeric: "tabular-nums" }}>{durata(x.minuti)}</b>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", letterSpacing: ".05em", margin: "14px 2px 4px" }}>BLOCCHI · TOCCA PER CORREGGERE</div>
          {blocchi === null ? <div style={{ fontSize: 13, color: "#9ca3af", padding: 8 }}>Carico…</div>
            : giornata.blocchi.length === 0 ? <div style={{ fontSize: 13, color: "#9ca3af", padding: "8px 2px" }}>Nessuna ora segnata {giorno === oggi ? "oggi" : "in questo giorno"}.</div>
            : giornata.blocchi.map(b => (
              <button key={b.id} type="button" onClick={() => { if (meseApprovato && !b.inCorso) { addToast("Il cartellino di questo mese è approvato: per correggere chiedi all'amministrazione", "info"); return; } setModifica(b); }}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "10px 6px", border: "none", borderTop: "1px solid #f5f3ff", background: "none", fontFamily: font, cursor: "pointer" }}>
                <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: colore(b.projectId) }} />
                <span style={{ width: 92, fontSize: 12.5, color: GRIGIO, fontVariantNumeric: "tabular-nums" }}>{b.inizio ? `${oraDi(b.inizio)} – ${b.inCorso ? "adesso" : oraDi(b.fine)}` : "a mano"}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: SCURO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.projectId ? etichetta({ codiceCommessa: b.codiceCommessa, name: b.projectNome }) : UFFICIO.nome}</span>
                  {b.nota ? <span style={{ display: "block", fontSize: 12, color: GRIGIO, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.nota}</span> : null}
                </span>
                <b style={{ fontSize: 13.5, color: b.inCorso ? VIOLA : SCURO, fontVariantNumeric: "tabular-nums" }}>{durata(minutiBlocco(b, adesso))}</b>
              </button>
            ))}
          {riscontroCartellino(giornata.totale, minutiPresenza) && !meseApprovato && (
            <div style={{ marginTop: 10, background: "#faf5ff", color: "#5b21b6", border: "1px solid #ddd6fe", borderRadius: 10, padding: "8px 12px", fontSize: 12.5, lineHeight: 1.45 }}>
              {riscontroCartellino(giornata.totale, minutiPresenza)}
            </div>
          )}
          {meseApprovato ? (
            <div style={{ marginTop: 10, fontSize: 12.5, color: GRIGIO, textAlign: "center", padding: "8px 4px" }}>
              🔒 Il cartellino di questo mese è approvato: le ore restano così. Per una correzione chiedi all'amministrazione.
            </div>
          ) : (
          <button type="button" onClick={() => setModifica({})}
            style={{ width: "100%", marginTop: 8, padding: "12px", borderRadius: 12, border: "1.5px dashed #c4b5fd", background: "#faf5ff", color: VIOLA, fontWeight: 800, fontSize: 14, fontFamily: font, cursor: "pointer" }}>
            + Aggiungi a mano
          </button>
          )}
        </div>
      </div>

      {modifica && (
        <FinestraBlocco blocco={modifica.id ? modifica : null} giorno={modifica.data || giorno} progetti={progetti}
          altri={(blocchi || []).filter(b => b.id !== modifica.id)}
          onChiudi={() => setModifica(null)} onSalva={salvaBlocco} onElimina={eliminaBlocco} />
      )}
    </div>
  );
}
